/**
 * Per-country section visibility, from the CMS only (AHD-13430).
 *
 * The CMS `section-visibility` global is the single source of which markets
 * show a `section:` gate (`COUNTRY_GATES` in `lib/country-gates.ts` holds no
 * country list for them). Each section carries an explicit "show" / "hide" per
 * market. There is no fallback to code: a section with no value for a market,
 * or a global that cannot be read at all (no live copy and no saved
 * last-known-good copy), throws an error naming the section and market.
 *
 * `page:` and `feature:` gates are not CMS-editable and still resolve from the
 * code registry.
 *
 * Pure and client-safe: the fetch lives in `lib/cms-server.ts`, the context in
 * `components/layout/section-visibility-provider.tsx`.
 */

import type { Country } from "@/i18n/config"
import {
  COUNTRY_GATES,
  isAllowedInCountry,
  type CodeGateId,
  type GateId,
  type SectionGateId,
} from "@/lib/country-gates"

export type { SectionGateId }

/** Every market, not `knownCountryCodes` (which drops flag-disabled ones). */
const MARKETS: readonly Country[] = ["ch", "nl", "de", "at"]

/** Only `section:` gates are editable; pages/features stay in code. */
export const SECTION_GATE_IDS = (Object.keys(COUNTRY_GATES) as GateId[]).filter(
  (id): id is SectionGateId => id.startsWith("section:")
)

/** `section:go/bloodtest.hero-cta2` → `go_bloodtest_hero_cta2`. Mirrors the CMS field name. */
export function sectionFieldName(id: SectionGateId): string {
  return id.slice("section:".length).replace(/[^A-Za-z0-9]+/g, "_")
}

/** The CMS answer per section and market (true = "show", false = "hide"). */
export type SectionVisibility = Partial<
  Record<SectionGateId, Partial<Record<Country, boolean>>>
>

/**
 * Turn the raw `/globals/section-visibility` JSON into per-market booleans.
 * Reads exactly "show" or "hide"; any other value on a known section/market
 * (e.g. the retired "inherit", a typo) throws. An absent value is not read here
 * — `resolveSectionVisibility` throws when that section/market is asked for.
 * Throws if the payload is not an object.
 */
export function parseSectionVisibility(raw: unknown): SectionVisibility {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    throw new Error(
      "section-visibility global is not an object; cannot read section visibility"
    )
  }
  const doc = raw as Record<string, unknown>
  const out: SectionVisibility = {}
  for (const id of SECTION_GATE_IDS) {
    const group = doc[sectionFieldName(id)]
    if (group === undefined || group === null) continue
    if (typeof group !== "object" || Array.isArray(group)) {
      throw new Error(
        `section-visibility: "${id}" must be an object of market values, got ${JSON.stringify(group)}`
      )
    }
    for (const country of MARKETS) {
      const v = (group as Record<string, unknown>)[country]
      if (v === undefined || v === null) continue
      if (v !== "show" && v !== "hide") {
        throw new Error(
          `section-visibility: "${id}" in market "${country}" must be "show" or "hide", got ${JSON.stringify(v)}`
        )
      }
      ;(out[id] ??= {})[country] = v === "show"
    }
  }
  return out
}

/**
 * Whether a gate shows in `country`. `section:` gates come from the CMS value
 * in `visibility` and throw when it has none for that market (or is `null`, the
 * global being unreadable); `page:`/`feature:` gates use the code registry.
 */
export function resolveSectionVisibility(
  id: GateId,
  country: Country,
  visibility: SectionVisibility | null | undefined
): boolean {
  if (!id.startsWith("section:"))
    return isAllowedInCountry(id as CodeGateId, country)
  const value = visibility?.[id as SectionGateId]?.[country]
  if (typeof value === "boolean") return value
  throw new Error(
    visibility
      ? `Section "${id}" has no show/hide value in the CMS section-visibility global for market "${country}"`
      : `Section "${id}" in market "${country}": the CMS section-visibility global could not be read (no live copy and no saved copy)`
  )
}

/** `(id, country) => boolean` over one CMS read, for server code that gates several sections. */
export function sectionGateFor(
  visibility: SectionVisibility | null | undefined
): (id: GateId, country: Country) => boolean {
  return (id, country) => resolveSectionVisibility(id, country, visibility)
}
